import { matchSorter } from 'match-sorter';
import { QueueRuleAction } from '@rossum/api-client/schemas';

export type SearchableRule = {
  id: number;
  name: string;
  description: string;
  triggerCondition: string;
  actions: QueueRuleAction[];
};

const MAX_PAYLOAD_DEPTH = 3;

const flattenPayload = (obj: unknown, depth = 0): string[] => {
  if (depth > MAX_PAYLOAD_DEPTH || obj == null) return [];

  if (typeof obj === 'string') return [obj];
  if (typeof obj === 'number') return [obj.toString()];
  if (typeof obj === 'boolean') return [];

  if (Array.isArray(obj)) {
    return obj.flatMap(item => flattenPayload(item, depth + 1));
  }

  if (typeof obj === 'object') {
    return Object.values(obj).flatMap(value =>
      flattenPayload(value, depth + 1)
    );
  }

  return [];
};

const extractActionSearchableText = (actions: QueueRuleAction[]): string => {
  return actions.flatMap(action => flattenPayload(action.payload)).join(' ');
};

export const searchInRules = <T extends SearchableRule>(
  searchValue: string,
  rules: T[]
): T[] => {
  const normalizedSearchValue = searchValue.trim();
  if (!normalizedSearchValue) return rules;

  return matchSorter(rules, normalizedSearchValue, {
    keys: [
      rule => String(rule.id),
      'name',
      'description',
      'triggerCondition',
      rule => extractActionSearchableText(rule.actions),
    ],
    threshold: matchSorter.rankings.CONTAINS,
  });
};
